package com.bulut.mecitogullari.ui.screens.transactions

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Build
import androidx.compose.material.icons.filled.List
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Share
import androidx.compose.material.icons.filled.ShoppingCart
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Tab
import androidx.compose.material3.TabRow
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.export.MonthReportHtml
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.importer.ExcelExport
import com.bulut.mecitogullari.core.platform.isExcelSupported
import com.bulut.mecitogullari.core.platform.isPrintSupported
import com.bulut.mecitogullari.core.platform.printHtml
import com.bulut.mecitogullari.core.platform.saveExcelFile
import com.bulut.mecitogullari.core.util.formatAmountPrecise
import com.bulut.mecitogullari.ui.components.EmptyState
import com.bulut.mecitogullari.ui.components.MonthSelector
import com.bulut.mecitogullari.ui.components.TransactionRow
import com.bulut.mecitogullari.ui.excel.ExcelImportDialogs
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun TransactionsScreen(
    onTransactionClick: (transactionId: String) -> Unit,
    onAddTransaction: () -> Unit,
    onOpenDebts: () -> Unit,
    onOpenCraftsmen: () -> Unit,
    viewModel: TransactionsViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val excelImportState by viewModel.excelImportState.collectAsStateWithLifecycle()
    val strings = LocalStrings.current

    ExcelImportDialogs(
        state = excelImportState,
        onConfirm = viewModel::confirmExcelImport,
        onDismiss = viewModel::dismissExcelImport,
    )

    Box(modifier = Modifier.fillMaxSize()) {
        Column(modifier = Modifier.fillMaxSize().statusBarsPadding()) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth().padding(start = 16.dp, end = 8.dp, top = 12.dp),
            ) {
                Text(
                    text = strings.tabTransactions,
                    style = MaterialTheme.typography.titleLarge,
                    modifier = Modifier.weight(1f),
                )
                if (isExcelSupported) {
                    var excelMenuOpen by remember { mutableStateOf(false) }
                    Box {
                        IconButton(onClick = { excelMenuOpen = true }) {
                            Icon(
                                Icons.Default.List,
                                contentDescription = strings.excelMenu,
                                tint = AppColors.TextSecondary,
                            )
                        }
                        DropdownMenu(
                            expanded = excelMenuOpen,
                            onDismissRequest = { excelMenuOpen = false },
                        ) {
                            DropdownMenuItem(
                                text = { Text(strings.exportExcel) },
                                enabled = uiState.monthTransactions.isNotEmpty(),
                                onClick = {
                                    excelMenuOpen = false
                                    saveExcelFile(
                                        fileName = ExcelExport.fileNameForMonth(uiState.month),
                                        rows = ExcelExport.rows(
                                            transactions = uiState.monthTransactions,
                                            projectNames = uiState.projectNames,
                                        ),
                                    )
                                },
                            )
                            DropdownMenuItem(
                                text = { Text(strings.importExcel) },
                                onClick = {
                                    excelMenuOpen = false
                                    viewModel.startExcelImport()
                                },
                            )
                        }
                    }
                }
                if (isPrintSupported && uiState.monthTransactions.isNotEmpty()) {
                    IconButton(
                        onClick = {
                            printHtml(
                                html = MonthReportHtml.build(
                                    summary = uiState.monthSummary,
                                    transactions = uiState.monthTransactions,
                                    projectNames = uiState.projectNames,
                                ),
                                fileName = "islemler-${uiState.month.year}-" +
                                    uiState.month.month.toString().padStart(2, '0') + ".pdf",
                            )
                        },
                    ) {
                        Icon(
                            Icons.Default.Share,
                            contentDescription = strings.downloadMonthPdf,
                            tint = AppColors.TextSecondary,
                        )
                    }
                }
            }

            MonthSelector(
                month = uiState.month,
                onPrevious = viewModel::onPreviousMonth,
                onNext = viewModel::onNextMonth,
                modifier = Modifier.padding(horizontal = 8.dp),
            )

            OutlinedTextField(
                value = uiState.query,
                onValueChange = viewModel::onQueryChange,
                placeholder = { Text(strings.transactionsSearchHint) },
                leadingIcon = { Icon(Icons.Default.Search, contentDescription = null, tint = AppColors.TextSecondary) },
                singleLine = true,
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            )

            val tabs = listOf(
                TransactionsTab.ALL to strings.tabAll,
                TransactionsTab.INCOME to strings.income,
                TransactionsTab.EXPENSE to strings.expense,
            )
            TabRow(
                selectedTabIndex = tabs.indexOfFirst { it.first == uiState.tab },
                containerColor = Color.Transparent,
                modifier = Modifier.padding(top = 8.dp),
            ) {
                tabs.forEach { (tab, title) ->
                    Tab(
                        selected = uiState.tab == tab,
                        onClick = { viewModel.onTabChange(tab) },
                        text = { Text(title) },
                    )
                }
            }

            if (uiState.transactions.isEmpty()) {
                EmptyState(
                    message = if (uiState.query.isBlank()) {
                        strings.noRecordsForMonth.fillTemplate("month" to uiState.month.label)
                    } else {
                        strings.noSearchMatch
                    },
                )
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 96.dp),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    items(uiState.transactions, key = { it.id }) { transaction ->
                        TransactionRow(
                            transaction = transaction,
                            projectName = transaction.projectId?.let { uiState.projectNames[it] },
                            onClick = { onTransactionClick(transaction.id) },
                        )
                    }
                }
            }
        }

        FloatingActionButton(
            onClick = onAddTransaction,
            containerColor = AppColors.Navy,
            contentColor = Color.White,
            modifier = Modifier.align(Alignment.BottomEnd).padding(20.dp),
        ) {
            Icon(Icons.Default.Add, contentDescription = strings.addTransaction)
        }
    }
}